/**
 * Referral codes.
 *
 * Clicks are folded in by the daily rollup and signups are written immediately,
 * so the two columns can differ by up to a day. The page states that instead of
 * presenting a stale click count as live.
 *
 * A collapsed row carries the code, its owner and the two counters; the opened
 * part adds the freshness of the last signup and the way into the signup list.
 */

import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { LoadingBlock, ErrorBlock } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { EmptyState } from '../../components/ui/primitives'
import { useReferrals } from '../../hooks/use-admin'
import { formatUnixSeconds } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage, CollapsibleRow, DetailRow, Pager, usePageParam } from './shared'

export function AdminReferralsPage() {
  const { t } = useTranslation()
  const { page, goToPage } = usePageParam()
  const locale = currentLocale()

  const referrals = useReferrals(page)

  const pager = referrals.data ? (
    <Pager
      page={referrals.data.page}
      pageSize={referrals.data.pageSize}
      total={referrals.data.total}
      onPage={goToPage}
    />
  ) : null

  return (
    <AdminPage
      title={t('admin.referrals.title')}
      description={t('admin.referrals.description')}
      current={t('admin.nav.referrals')}
    >
      {referrals.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {referrals.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void referrals.refetch()}
        />
      ) : null}
      {referrals.data && referrals.data.items.length === 0 ? (
        <EmptyState title={t('admin.referrals.empty')} />
      ) : null}

      {pager}

      <div className="flex flex-col gap-2">
        {referrals.data?.items.map((item) => (
          <CollapsibleRow
            key={item.code}
            title={<span className="font-mono text-sm">{item.code}</span>}
            meta={
              <span className="text-muted-foreground truncate text-xs">
                {item.ownerEmail ?? t('admin.referrals.unknownOwner')}
                {item.ownerNickname ? ` · ${item.ownerNickname}` : ''}
              </span>
            }
            badges={
              <>
                <span className="text-xs">{t('admin.referrals.clicks', { count: item.clicks })}</span>
                <span className="text-xs">
                  {t('admin.referrals.signups', { count: item.signups })}
                </span>
              </>
            }
          >
            <DetailRow label={t('admin.referrals.lastSignupLabel')}>
              {item.lastSignupAt === null
                ? t('admin.referrals.noSignupYet')
                : formatUnixSeconds(item.lastSignupAt, locale)}
            </DetailRow>
            <DetailRow label={t('admin.referrals.clicksAggregatedUntilLabel')}>
              {formatUnixSeconds(item.updatedAt, locale)}
            </DetailRow>
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to={`/admin/referrals/${item.code}`}>{t('admin.referrals.open')}</Link>
              </Button>
            </div>
          </CollapsibleRow>
        ))}
      </div>

      {pager}
    </AdminPage>
  )
}
